import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Heart, MessageSquare, Send, Calendar, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';
import CoachNoteCard from '../../components/CoachNoteCard';
import FocusPromptList from '../../components/FocusPromptList';
import VoiceMemoRecorder from '../../components/VoiceMemoRecorder';

const StatCard = ({ kpi, icon: Icon, color, stats }) => (
  <div className="bg-white border border-[#E7E5E4] p-4" data-testid={`stat-${kpi}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className="w-8 h-8 rounded-full text-white flex items-center justify-center" style={{ backgroundColor: color }}>
        <Icon className="w-4 h-4" />
      </span>
      <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">{kpi}</p>
    </div>
    <p className="font-['Playfair_Display'] text-3xl tabular-nums" style={{ color }}>
      {stats?.latest ?? '—'}<span className="text-sm text-[#737373]"> / 10</span>
    </p>
    <p className="text-xs text-[#57534E] mt-1">
      {stats?.total ? `${stats.total} session${stats.total === 1 ? '' : 's'} · avg ${stats.avg}` : 'No data'}
    </p>
  </div>
);

const RoundRow = ({ r, studentId, onNote }) => (
  <li className="bg-white border border-[#E7E5E4] p-4" data-testid={`coach-round-${r.id}`}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="min-w-0">
        <p className="font-semibold text-[#1A1A1A]">{r.course_name || 'Round'}</p>
        <p className="text-xs text-[#737373]">{new Date(r.round_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
      </div>
      <div className="text-right">
        {r.gap_aa != null && (
          <span className="text-[10px] uppercase tracking-wider bg-[#1A2E1D] text-white px-2 py-1">Gap {r.gap_aa > 0 ? `+${r.gap_aa}` : r.gap_aa}</span>
        )}
      </div>
    </div>
    <div className="flex flex-wrap gap-3 text-xs">
      <span className="inline-flex items-center gap-1 text-[#2C5530] font-semibold"><Brain className="w-3.5 h-3.5" /> {r.overall_awareness ?? '—'}/10</span>
      <span className="inline-flex items-center gap-1 text-[#BC562B] font-semibold"><Heart className="w-3.5 h-3.5" /> {r.overall_acceptance ?? '—'}/10</span>
      <span className="inline-flex items-center gap-1 text-[#1A2E1D] font-semibold"><ArrowRight className="w-3.5 h-3.5" /> {r.overall_action ?? '—'}/10</span>
    </div>
    {r.overall_reflection && (
      <p className="text-sm text-[#57534E] mt-3 whitespace-pre-wrap border-t border-[#F2F0E9] pt-3">{r.overall_reflection}</p>
    )}
    <button
      onClick={() => onNote(r.id)}
      className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A2E1D] hover:text-[#0F1F12]"
      data-testid={`note-on-round-${r.id}`}
    >
      <MessageSquare className="w-3.5 h-3.5" /> Add coach note
    </button>
  </li>
);

const CoachLockerStudentDetailPage = () => {
  const { studentId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNoteRoundId, setActiveNoteRoundId] = useState(null);
  const [generalNote, setGeneralNote] = useState('');
  const [roundNote, setRoundNote] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        API.get(`/coach/locker/students/${studentId}`),
        API.get(`/coach/locker/students/${studentId}/streak`).catch(() => ({ data: null })),
      ]);
      setData(d.data);
      setStreak(s.data);
    } catch {
      toast.error('Failed to load student.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);

  const postNote = async ({ note, round_review_id }) => {
    setPosting(true);
    try {
      await API.post('/coach/locker/notes', {
        player_id: studentId,
        note,
        round_review_id: round_review_id || null,
      });
      toast.success('Note sent to player.');
      if (round_review_id) {
        setActiveNoteRoundId(null);
        setRoundNote('');
      } else {
        setGeneralNote('');
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post note.');
    } finally {
      setPosting(false);
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await API.delete(`/coach/locker/notes/${id}`);
      toast.success('Note deleted.');
      load();
    } catch {
      toast.error('Failed to delete note.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { student, stats, round_reviews, coach_notes } = data;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="coach-student-detail">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-5xl flex items-center justify-between">
          <Link to="/coach/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-students">
            <ArrowLeft className="w-4 h-4" /> All players
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-5xl">
        {/* Player header */}
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Coach view</p>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A]">{student.full_name}</h1>
          <p className="text-[#57534E] mt-1 text-sm">{student.email} · {student.subscription_tier || 'free'}{student.handicap != null && ` · Handicap ${student.handicap}`}</p>
          {streak && (streak.current_streak > 0 || streak.longest_streak > 0) && (
            <div className="mt-3 inline-flex items-center gap-3 text-xs" data-testid="student-streak">
              <span className="inline-flex items-center gap-1.5 bg-[#FFF4ED] text-[#BC562B] border border-[#BC562B]/30 px-2.5 py-1 font-semibold">
                <Flame className="w-3.5 h-3.5" /> {streak.current_streak}-day streak
              </span>
              <span className="text-[#737373]">Longest: {streak.longest_streak} day{streak.longest_streak === 1 ? '' : 's'}</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard kpi="awareness" icon={Brain} color="#2C5530" stats={stats.awareness} />
          <StatCard kpi="acceptance" icon={Heart} color="#BC562B" stats={stats.acceptance} />
          <StatCard kpi="action" icon={ArrowRight} color="#1A2E1D" stats={stats.action} />
        </div>

        {/* Coach-assigned focus prompts */}
        <div className="mb-8">
          <FocusPromptList mode="coach" playerId={studentId} title="Daily focus prompts" subtitle="Set up to 3 prompts. Player ticks each one off daily." />
        </div>

        {/* General coach note */}
        <div className="bg-white border border-[#E7E5E4] p-5 mb-8" data-testid="general-note-form">
          <h2 className="font-semibold text-[#1A1A1A] mb-2 flex items-center gap-2"><Send className="w-4 h-4" /> Leave a general note</h2>
          <textarea
            value={generalNote}
            onChange={(e) => setGeneralNote(e.target.value)}
            placeholder="A coaching cue, encouragement or theme to focus on..."
            rows={3}
            className="w-full mb-2"
            data-testid="general-note-textarea"
          />
          <div className="flex justify-end">
            <button
              onClick={() => generalNote.trim() && postNote({ note: generalNote.trim() })}
              disabled={posting || !generalNote.trim()}
              className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1E3A21] disabled:opacity-50"
              data-testid="send-general-note-btn"
            >
              <Send className="w-3.5 h-3.5" /> Send
            </button>
          </div>
          <div className="mt-3">
            <VoiceMemoRecorder playerId={studentId} onSent={load} label="Voice memo" />
          </div>
        </div>

        {/* Round reviews */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-[#1A2E1D]" />
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Round reviews · {round_reviews?.length || 0}</h2>
          </div>
          {round_reviews?.length ? (
            <ul className="space-y-3">
              {round_reviews.map((r) => (
                <React.Fragment key={r.id}>
                  <RoundRow r={r} studentId={studentId} onNote={(id) => { setActiveNoteRoundId(id); setRoundNote(''); }} />
                  {activeNoteRoundId === r.id && (
                    <li className="bg-[#FAFAF9] border border-[#BC562B] p-4 -mt-3" data-testid={`round-note-form-${r.id}`}>
                      <textarea
                        value={roundNote}
                        onChange={(e) => setRoundNote(e.target.value)}
                        placeholder={`A note on this round at ${r.course_name || 'this round'}...`}
                        rows={3}
                        className="w-full mb-2"
                        data-testid={`round-note-textarea-${r.id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setActiveNoteRoundId(null)} className="text-sm text-[#737373] px-3 py-2 hover:text-[#1A1A1A]">Cancel</button>
                        <button
                          onClick={() => roundNote.trim() && postNote({ note: roundNote.trim(), round_review_id: r.id })}
                          disabled={posting || !roundNote.trim()}
                          className="inline-flex items-center gap-2 bg-[#1A2E1D] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0F1F12] disabled:opacity-50"
                          data-testid={`send-round-note-btn-${r.id}`}
                        >
                          <Send className="w-3.5 h-3.5" /> Send note
                        </button>
                      </div>
                      <div className="mt-3">
                        <VoiceMemoRecorder playerId={studentId} roundReviewId={r.id} onSent={load} label="Voice memo for this round" />
                      </div>
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#A8A29E] bg-white border border-[#E7E5E4] p-5">No unified round reviews yet from this player.</p>
          )}
        </div>

        {/* Your notes log */}
        <div className="bg-white border border-[#E7E5E4] p-5" data-testid="coach-notes-log">
          <h2 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Notes you've sent</h2>
          {coach_notes?.length ? (
            <ul className="space-y-4">
              {coach_notes.map((n) => (
                <li key={n.id} data-testid={`existing-note-${n.id}`}>
                  <CoachNoteCard
                    note={n}
                    currentUserId={user?.id}
                    onDeleteNote={deleteNote}
                    onChange={(updated) => setData((d) => ({
                      ...d,
                      coach_notes: d.coach_notes.map((x) => x.id === updated.id ? updated : x),
                    }))}
                  />
                  {n.round_review_id && (
                    <p className="mt-1 ml-4 text-[10px] uppercase tracking-wider text-[#1A2E1D]/60">attached to a round review</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#A8A29E]">No notes sent yet.</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachLockerStudentDetailPage;
